import {
  DetailedIconKeyHome,
  DetailedIconPaperAirplane,
  DetailedIconPiggybank,
  Heading,
  MediaObject,
  Paragraph,
  Spacer,
} from '@zillow/constellation';

import {
  StyledBackground,
  StyledSpacerMediaObjectWrapper,
  StyledSpacerUpsellInfoWrapper,
  StyledTextUpsellInfo,
} from './StyledComponents';
import { DetailedHomeSparkle } from '../HomeSparkle';
import ZillowSpotIllustrations from '../ZillowSpotIllustrations';

import type { UpsellInfo } from '../../types';

type UpsellBodyProps = {
  upsellInfo: UpsellInfo;
};

const UpsellBody = ({ upsellInfo }: UpsellBodyProps) => {
  const isInsuranceRequired = upsellInfo.isInsuranceRequiredForLease || upsellInfo.isInsuranceRequiredForListing;
  const upsellBodyElements = [
    {
      icon: isInsuranceRequired ? <DetailedIconPaperAirplane /> : <DetailedIconKeyHome />,
      text: isInsuranceRequired ? 'Get coverage in minutes' : 'Plan for your move',
      paragraph: isInsuranceRequired
        ? 'You can even choose to auto-send proof of coverage to your landlord.'
        : 'Get covered in less time than it takes to find movers.',
    },
    {
      icon: <DetailedIconPiggybank />,
      text: `Popular plans start at ${upsellInfo.monthlyPriceEstimate}/mo`,
      paragraph: `Join renters near ${upsellInfo.zip}, who protect their personal items from events like theft or damage.`,
    },
    {
      icon: <DetailedHomeSparkle />,
      text: 'Protect the things you love',
      paragraph: 'Customize your plan with coverage for high value items, such as your laptop, jewelry, and more.',
    },
  ];

  const getSubheadingText = () => {
    if (upsellInfo.isInsuranceRequiredForLease) {
      return 'Your lease requires insurance';
    } else if (upsellInfo.isInsuranceRequiredForListing) {
      return 'Your landlord requires insurance';
    }

    return 'Get coverage in minutes';
  };

  return (
    <Spacer data-testid="upsell-modal-upsell-info" paddingBottom="md">
      <StyledBackground>
        <ZillowSpotIllustrations width="150" height="150" />
        <Heading level="4" fontFamily="serif" marginBottom="xs">
          Renters insurance
        </Heading>
        <Paragraph fontType="body" marginBottom="sm">
          {getSubheadingText()}
        </Paragraph>
      </StyledBackground>
      <Spacer marginX={{ default: 'xl', md_lte: 'sm' }} paddingX={{ default: 'xl', md_lte: 'sm' }}>
        {upsellBodyElements.map((element, index) => (
          <StyledSpacerMediaObjectWrapper key={index}>
            <MediaObject media={element.icon} align="start">
              <StyledSpacerUpsellInfoWrapper>
                <StyledTextUpsellInfo>{element.text}</StyledTextUpsellInfo>
                <Paragraph fontColor="coolGray500" fontType="body">
                  {element.paragraph}
                </Paragraph>
              </StyledSpacerUpsellInfoWrapper>
            </MediaObject>
          </StyledSpacerMediaObjectWrapper>
        ))}
      </Spacer>
    </Spacer>
  );
};

export default UpsellBody;
